<template>
  <div class="relative flex flex-1 flex-col">
    <div
      v-if="!glowFailed"
      class="pointer-events-none absolute -inset-x-[200px] -top-[200px] -bottom-[300px]"
    >
      <CheckerGlow
        :anchor="button ?? null"
        :pressed="pressed"
        :active="checker.mode.value === 'audio'"
        @ready="glowReady = true"
        @failure="handleGlowFailure"
      />
    </div>

    <CheckerPermission
      v-if="checker.microphone.value === 'denied'"
      kind="microphone"
      class="absolute inset-x-0 top-0 z-10 bg-white"
    />

    <div class="relative z-10 flex flex-1 flex-col items-center justify-center">
      <h3
        class="text-center text-[24px] leading-[28px] font-medium text-gray-800"
        :class="checker.microphone.value === 'denied' && 'invisible'"
      >
        {{ heading }}
      </h3>
      <button
        ref="button"
        type="button"
        :aria-label="checker.live.value ? 'Stop listening' : 'Start listening'"
        :disabled="checker.live.value?.phase === 'draining'"
        class="mt-[30px] size-[180px] cursor-pointer rounded-full transition-transform duration-100 disabled:pointer-events-none"
        :class="showFallback && pressed && 'scale-[0.92]'"
        @pointerdown="pressed = true"
        @pointerup="pressed = false"
        @pointerleave="pressed = false"
        @pointercancel="pressed = false"
        @click="checker.toggleLive(false)"
      >
        <span
          v-if="showFallback"
          class="flex size-full items-center justify-center rounded-full border-[0.5px] border-[#dbdbdb] bg-white shadow-[0_8px_26px_0_rgba(0,0,0,0.1)]"
        >
          <DacrMarkIcon
            class="size-[130px] text-[#2a2b2c]"
            aria-hidden="true"
          />
        </span>
      </button>

      <div
        class="absolute inset-x-0 flex justify-center"
        :class="variant === 'modal' ? 'bottom-[27px]' : 'bottom-10'"
      >
        <Transition
          enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          enter-from-class="scale-0"
          leave-active-class="transition-transform duration-150 ease-in"
          leave-to-class="scale-0"
        >
          <div v-if="checker.findingCount.value">
            <CheckerMatchesPill
              :count="checker.findingCount.value"
              @review="checker.review"
            />
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { DacrMarkIcon } from '@dacr/icons'

import CheckerGlow from './CheckerGlow.vue'
import CheckerMatchesPill from './CheckerMatchesPill.vue'
import CheckerPermission from './CheckerPermission.vue'
import { useChecker } from './useChecker'

const props = defineProps<{ variant: 'modal' | 'sheet' }>()

const checker = useChecker()
const button = ref<HTMLButtonElement>()
const pressed = ref(false)
const glowReady = ref(false)
const glowFailed = ref(false)
const glowSlow = ref(false)

const slowTimer = window.setTimeout(() => {
  if (!glowReady.value) glowSlow.value = true
}, 1500)

onBeforeUnmount(() => window.clearTimeout(slowTimer))

const showFallback = computed(
  () => glowFailed.value || (glowSlow.value && !glowReady.value)
)

const handleGlowFailure = () => {
  glowFailed.value = true
  glowReady.value = false
}

const heading = computed(() => {
  if (checker.requesting.value) return 'Starting...'
  switch (checker.live.value?.phase) {
    case 'starting':
      return 'Starting...'
    case 'waiting':
      return 'Waiting for capacity...'
    case 'recording':
      return 'Listening...'
    case 'draining':
      return 'Checking...'
    default:
      return props.variant === 'modal' ? 'Press to listen' : 'Tap to listen'
  }
})
</script>
